/**
 * TeamSystem Impact - Compliance Path · Raccolta Dati drill-down
 *
 * Elenco dei report (campagne) con due barre di avanzamento (Compilati/Validati);
 * al clic su un report si espandono inline i KPI Chiave di quella campagna.
 */

import { useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import { OpenInNew as OpenInNewIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { CompliancePathData } from './useCompliancePath';
import { KpiDashboardTab } from '../../data-capture/components/KpiDashboardTab';

const pct = (n: number, total: number) => (total > 0 ? Math.round((n / total) * 100) : 0);

export const CollectionDrill = ({ data }: { data: CompliancePathData['collection'] }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [openId, setOpenId] = useState<string | null>(null);

  const renderBar = (label: string, value: number, count: string) => (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.25}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="caption" color="text.secondary">{count}</Typography>
      </Box>
      <LinearProgress variant="determinate" value={value} sx={{ height: 5, borderRadius: 3 }} />
    </Box>
  );

  return (
    <Stack spacing={2}>
      {data.campaigns.length > 0 ? (
        <Box>
          <Typography variant="subtitle2" gutterBottom>{t('dashboard.path.colReports')}</Typography>
          <Stack spacing={1}>
            {data.campaigns.map((entry) => {
              const id = entry.campaign._id ?? entry.campaign.name;
              const { total, compiled, completed } = entry.kpi;
              const open = openId === id;
              const toggle = () => setOpenId(open ? null : id);
              return (
                <Box
                  key={id}
                  data-testid="collection-campaign"
                  role="button"
                  tabIndex={0}
                  onClick={toggle}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); }
                  }}
                  sx={{
                    borderRadius: 1,
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    p: 1.25,
                    cursor: 'pointer',
                    '&:hover': { borderColor: 'primary.main' },
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1} sx={{ mb: 0.75 }}>
                    <Typography variant="body2" fontWeight={600} noWrap sx={{ flexGrow: 1, minWidth: 0 }}>
                      {entry.campaign.name}
                    </Typography>
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        color: 'text.secondary',
                        transition: 'transform 0.2s',
                        transform: open ? 'rotate(180deg)' : 'none',
                      }}
                    />
                  </Box>
                  <Stack spacing={0.75}>
                    {renderBar(t('dashboard.path.colCompiled'), pct(compiled, total), `${compiled}/${total}`)}
                    {renderBar(t('dashboard.path.colValidated'), entry.kpi.completionRate, `${completed}/${total}`)}
                  </Stack>
                  <Collapse in={open} timeout="auto" unmountOnExit>
                    <Box
                      data-testid="campaign-kpi-inline"
                      onClick={(e) => e.stopPropagation()}
                      sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}
                    >
                      <KpiDashboardTab campaignId={entry.campaign._id ?? ''} campaignName={entry.campaign.name} />
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
          </Stack>
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">{t('dashboard.path.colEmpty')}</Typography>
      )}

      <Box>
        <Button size="small" endIcon={<OpenInNewIcon />} onClick={() => navigate('/data/report?tab=kpi')}>
          {t('dashboard.path.colOpenReport')}
        </Button>
      </Box>
    </Stack>
  );
};
